
import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { Toaster as HotToaster } from "react-hot-toast"
import { AuthProvider } from "@/contexts/AuthContext"
import { AlertProvider } from "@/contexts/AlertContext"
import { ThemeProvider } from "@/contexts/ThemeContext"
import { WebSocketProvider } from "@/providers/WebSocketProvider"
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout";
import { ErrorBoundary } from "@/components/shared";
import { Login } from "@/pages/auth/Login";
import { SignUp } from "@/pages/auth/SignUp";
import RequestAccess from "@/pages/auth/RequestAccess";
import { VerifyEmail } from "@/pages/auth/VerifyEmail";
import { ForgotPassword } from "@/pages/auth/ForgotPassword";
import { Unauthorized } from "@/pages/auth/Unauthorized";
import { Dashboard } from "@/pages/Dashboard";
import { Leads } from "@/pages/Leads";
import { RuleManagement } from "@/pages/RuleManagement";
import { AlertManagement } from "@/pages/AlertManagement";
import { Analytics } from "@/pages/Analytics";
import { ExportReports } from "@/pages/ExportReports";
import { Settings } from "@/pages/Settings";
import Documentation from "@/pages/Documentation";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient()

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <HotToaster position="top-right" />
          <BrowserRouter>
            <AuthProvider>
              <WebSocketProvider>
                <AlertProvider>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/auth/login" element={<Login />} />
                    <Route path="/auth/request-access" element={<RequestAccess />} />
                    <Route path="/auth/signup" element={<Navigate to="/auth/request-access" replace />} />
                    <Route path="/auth/verify-email" element={<VerifyEmail />} />
                    <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    
                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute />}>
                      <Route element={<AppLayout />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/leads" element={<Leads />} />
                        <Route path="/rules" element={<RuleManagement />} />
                        <Route path="/alerts" element={<AlertManagement />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/exports" element={<ExportReports />} />
                        <Route path="/patterns" element={<div className="p-6">Patterns Page - Coming Soon</div>} />
                        <Route path="/docs" element={<Documentation />} />
                        <Route path="/settings" element={<Settings />} />
                      </Route>
                    </Route>
                    
                    {/* Admin Routes */}
                    <Route element={<ProtectedRoute requiredRoles={['owner', 'admin']} />}>
                      <Route element={<AppLayout />}>
                        <Route path="/team" element={<div className="p-6">Team Page - Coming Soon</div>} />
                        <Route path="/billing" element={<div className="p-6">Billing Page - Coming Soon</div>} />
                      </Route>
                    </Route>
                    
                    {/* Default Routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AlertProvider>
              </WebSocketProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
)

export default App;
